/**
 * useSolePropDerivation — Sole Proprietor cohort derivation engine.
 *
 * Analogue of `useIndividualDerivation` and `useBusinessDerivation`, tuned to
 * the Sole Prop wire locks per memory `project_business_procedures_full_lock_20260506`:
 *
 *   - customerType   : ALWAYS 'Individual' (long form). Sole Prop is a PERSON
 *                      with a DBA overlay on the account, not a business entity.
 *   - callClass      : ALWAYS '26'  (Personal DBA partition).
 *   - tinCode        : ALWAYS 'S'   (owner's SSN — no EIN).
 *   - cifField39     : ALWAYS 'Y'   (Consumer).
 *   - cifField8/37   : owner's actual first+last name (Personal CIF behavior).
 *   - acctField84    : `<Business Name> DBA`  (computed in deriveField84).
 *
 * Shape mirrors `BusinessDerivationResult` so the section/footer code can
 * consume the same chip + supervisor-banner conventions.
 */

import { useMemo } from 'react';
import type { RaoPerson } from '../../../types';
import { deriveField84, SOLE_PROP_WIRE } from './sole-prop-config';

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export interface SolePropDerivationResult {
  /** Locked customer type — Sole Prop is structurally Personal. */
  readonly customerType: 'Individual';
  /** Where the derivation came from — `locked` because Sole Prop has no picker. */
  readonly derivedFrom: 'locked';
  /** Locked Field 92 call class. */
  readonly callClass: typeof SOLE_PROP_WIRE.callClass;
  /** Locked Field 39 consumer flag. */
  readonly cifField39: typeof SOLE_PROP_WIRE.cifField39;
  /** Locked TIN code. */
  readonly tinCode: typeof SOLE_PROP_WIRE.tinCode;
  /** Live preview of the Account Field 84 overlay (rendered live as FSR types). */
  readonly acctField84Preview: string;
  /** Owner's full display name (used in cart-people-list rendering). */
  readonly displayName: string;
  /**
   * Sole Prop never requires supervisor approval — included for protocol
   * parity with Business / Trust derivation results.
   */
  readonly requiresSupervisorApproval: false;
}

export interface UseSolePropDerivationOptions {
  readonly mode: 'new' | 'existing';
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Derive the Sole-Prop view from current form state. All wire values are
 * constant locks; only `acctField84Preview` + `displayName` recompute on
 * input change.
 */
export function useSolePropDerivation(
  person: RaoPerson,
  _opts: UseSolePropDerivationOptions,
): SolePropDerivationResult {
  return useMemo<SolePropDerivationResult>(() => {
    const businessName = (person.name2 ?? '').trim();
    const acctField84Preview = deriveField84(businessName);

    const fn = person.firstName ?? '';
    const ln = person.lastName ?? '';
    const ownerDisplay = [fn, ln].filter((x) => !!x).join(' ').trim();
    // Render the cart-row label as "<Owner>  (DBA <Business>)" when both
    // exist so the FSR sees both identities in the people list.
    const displayName = businessName
      ? ownerDisplay
        ? `${ownerDisplay} (DBA ${businessName})`
        : `DBA ${businessName}`
      : ownerDisplay;

    return {
      customerType: 'Individual',
      derivedFrom: 'locked',
      callClass: SOLE_PROP_WIRE.callClass,
      cifField39: SOLE_PROP_WIRE.cifField39,
      tinCode: SOLE_PROP_WIRE.tinCode,
      acctField84Preview,
      displayName,
      requiresSupervisorApproval: false,
    };
  }, [person.firstName, person.lastName, person.name2]);
}
